import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Table,
    Badge,
    Button,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Pagination,
    PaginationItem,
    PaginationLink,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import CustomDropdown from "components/Common/CustomDropdown";
import CustomPagination from "components/Common/CustomPagination";
import { getTourPackagesDetail, getTourPackageById, togglePackageStatus } from "../../api/ApiServices";
import Swal from "sweetalert2";

const ITEMS_PER_PAGE = 10;

const getTourImages = (pkg) => {
    if (!pkg) return [];

    const rawPhotos = pkg.tourPhoto || pkg.photos || pkg.images || pkg.tourPhotos || pkg.image;
    if (!rawPhotos) return [];

    let list = [];
    if (Array.isArray(rawPhotos)) {
        list = rawPhotos;
    } else if (typeof rawPhotos === 'string') {
        const trimmed = rawPhotos.trim();
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
                list = JSON.parse(trimmed);
            } catch (e) {
                list = [trimmed];
            }
        } else if (trimmed.includes(',')) {
            list = trimmed.split(',').map((s) => s.trim()).filter(Boolean);
        } else if (trimmed) {
            list = [trimmed];
        }
    }

    return list
        .map((img) => {
            if (!img) return null;
            let str = String(img).trim().replace(/^["']|["']$/g, '');
            if (!str) return null;
            if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:')) {
                return str;
            }
            const cleanPath = str.replace(/^\/+/, '');
            if (cleanPath.startsWith('tours/')) {
                return `https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/${cleanPath}`;
            }
            return `https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/tours/${cleanPath}`;
        })
        .filter(Boolean);
};

const TourPackages = () => {
    const [tourPackages, setTourPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [viewLoading, setViewLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);

    // Fetch tour packages from API
    const fetchTourPackages = async (page = 1, search = "") => {


        setLoading(true);

        try {
            const requestParams = {
                page: page,
                limit: ITEMS_PER_PAGE,
                search: search
            };


            const response = await getTourPackagesDetail(requestParams);



            if (response?.success) {

                console.log("API SUCCESS - Transforming packages...");

                const transformedPackages = (response.data || []).map((pkg, index) => {



                    return {
                        id: pkg._id,
                        uuid: pkg.uuid,

                        // API fields
                        name: pkg.packageName || "Unknown Package",
                        destination: pkg.location || "No Data Found",
                        price: pkg.price || 0,
                        shortDescription:
                            pkg.shortDescription || "No description available.",

                        image: pkg.tourPhoto || "",

                        status: pkg.isActive ? "active" : "inactive",

                        createdAt: pkg.createdAt || null,
                        updatedAt: pkg.updatedAt || null,

                        // Guide details
                        guide: {
                            id: pkg.guide?._id || null,
                            uuid: pkg.guide?.uuid || "",
                            name: pkg.guide?.fullName || "No Data Found",
                            email: pkg.guide?.email || "No Data Found",
                            mobileNumber: pkg.guide?.mobileNumber || "No Data Found",
                            country: pkg.guide?.country || "No Data Found",
                            gender: pkg.guide?.gender || "No Data Found",
                            profileImage: pkg.guide?.profileImage || "",
                            type: pkg.guide?.type || "guide",
                            isActive: pkg.guide?.isActive ?? false
                        },

                        // Category details
                        category: {
                            id: pkg.category?._id || null,
                            uuid: pkg.category?.uuid || pkg.categoryUuid || "",
                            name: pkg.category?.name || "No Data Found",
                            isActive: pkg.category?.isActive ?? false
                        },

                        categoryUuid: pkg.categoryUuid || "",

                        // Places
                        places: pkg.places || [],

                        // Keep these if component requires them
                        duration: pkg.duration || "No Data Found",
                        currency: pkg.currency || "USD",
                        rating: pkg.rating || 0,
                        reviews: pkg.reviews || 0,
                        type: pkg.type || "General",
                        difficulty: pkg.difficulty || "Easy",
                        startDate: pkg.startDate || null,
                        endDate: pkg.endDate || null,
                        inclusions: pkg.inclusions || [],
                        exclusions: pkg.exclusions || [],
                        description:
                            pkg.description ||
                            pkg.shortDescription ||
                            "No description available.",
                        maxGroupSize: pkg.maxGroupSize || 0,
                        availableSpots: pkg.availableSpots || 0,
                        highlights: pkg.highlights || []
                    };
                });

                setTourPackages(transformedPackages);

                const totalRecords =
                    response.pagination?.totalRecords ||
                    transformedPackages.length;

                const totalPages =
                    response.pagination?.totalPages || 1;

                const currentPage =
                    response.pagination?.currentPage || 1;



                setTotalItems(totalRecords);
                setTotalPages(totalPages);
                setCurrentPage(currentPage);

            } else {



                setTourPackages([]);
                setTotalItems(0);
                setTotalPages(1);
            }

        } catch (error) {


            setTourPackages([]);
            setTotalItems(0);
            setTotalPages(1);

        } finally {

            setLoading(false);


        }
    };
    // Fetch single tour package details
    const fetchTourPackageDetails = async (uuid) => {
        try {
            setViewLoading(true);


            const response = await getTourPackageById(uuid);

            console.log("API RESPONSE:", response);
            console.log("API DATA:", response?.data);
            console.log("GUIDE DATA:", response?.data?.guide);

            if (response?.success && response?.data) {
                const pkg = response.data;

                const packageDetail = {
                    id: pkg._id,
                    uuid: pkg.uuid,

                    packageName: pkg.packageName || "No Data Found",
                    location: pkg.location || "No Data Found",
                    price: pkg.price || 0,
                    shortDescription: pkg.shortDescription || "",

                    tourPhoto: pkg.tourPhoto || "",

                    isActive: pkg.isActive ?? false,

                    places: Array.isArray(pkg.places)
                        ? pkg.places
                        : [],

                    createdAt: pkg.createdAt || null,
                    updatedAt: pkg.updatedAt || null,

                    // Category
                    category: pkg.category || null,

                    // Guide / User
                    guide: pkg.guide
                        ? {
                            _id: pkg.guide._id,
                            uuid: pkg.guide.uuid,
                            fullName: pkg.guide.fullName || "No Data Found",
                            email: pkg.guide.email || "No Data Found",
                            mobileNumber: pkg.guide.mobileNumber || "No Data Found",
                            country: pkg.guide.country || "No Data Found",
                            type: pkg.guide.type || "guide",
                            gender: pkg.guide.gender || "No Data Found",
                            profileImage: pkg.guide.profileImage || "",
                            coverImage: pkg.guide.coverImage || "",
                            isActive: pkg.guide.isActive ?? false
                        }
                        : null
                };



                setSelectedPackage(packageDetail);
                setViewModalOpen(true);
            } else {
                console.error(
                    "Failed to fetch package details:",
                    response?.message
                );
            }
        } catch (error) {
            console.error("Error fetching package details:", error);
        } finally {
            setViewLoading(false);
        }
    };
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            fetchTourPackages(currentPage, searchTerm);
        }, 300); // Debounce search

        return () => clearTimeout(delayDebounce);
    }, [currentPage, searchTerm]);

    const getFilteredPackages = () => {
        if (!searchTerm) return tourPackages;

        const lowerSearch = searchTerm.toLowerCase();
        return tourPackages.filter(pkg =>
            pkg.name.toLowerCase().includes(lowerSearch) ||
            pkg.destination.toLowerCase().includes(lowerSearch) ||
            pkg.type.toLowerCase().includes(lowerSearch) ||
            pkg.guide.name.toLowerCase().includes(lowerSearch) ||
            pkg.difficulty.toLowerCase().includes(lowerSearch)
        );
    };

    const filteredPackages = getFilteredPackages();
    const displayTotalItems = filteredPackages.length;
    const displayTotalPages = Math.ceil(displayTotalItems / ITEMS_PER_PAGE);

    // Get current page data
    const getCurrentPageData = () => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredPackages.slice(startIndex, endIndex);
    };

    const currentPackages = getCurrentPageData();

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const openViewModal = (pkg) => {
        setSelectedPackage(pkg);
        setViewModalOpen(true);
        // Fetch full details in background
        fetchTourPackageDetails(pkg.uuid);
    };

    const closeViewModal = () => {
        setViewModalOpen(false);
        setSelectedPackage(null);
    };

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return 'No Data Found';
        return new Date(dateString).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    // Format currency
    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };



    const handleTogglePackageStatus = async (uuid) => {
        try {
            const response = await togglePackageStatus(uuid);

            if (response?.success) {
                fetchTourPackages(currentPage, searchTerm);
            }
        } catch (error) {
            console.error(
                "Toggle package status error:",
                error.response?.data || error.message
            );
        }
    };

    return (
        <>
            <CommonHeader />

            <Container className="mt--7 tour-packages-module" fluid>
                <Row>
                    <Col>
                        <Card className="tour-packages-management-card shadow border-0">
                            <CardHeader className="tour-packages-card-header border-0">
                                <div className="tour-packages-header-content">
                                    <div className="tour-packages-header-icon">
                                    </div>
                                    <div>

                                        <h3 className="tour-packages-header-title">
                                            Tour Packages Managment
                                        </h3>

                                    </div>
                                </div>
                                <div className="tour-packages-header-actions">
                                    <Badge color="primary" pill className="p-2 px-3">
                                        Total: {totalItems}
                                    </Badge>
                                </div>
                            </CardHeader>

                            {/* ================= BODY ================= */}
                            <CardBody className="tour-packages-card-body">
                                {/* SEARCH */}
                                <div className="tour-packages-search-wrapper mb-3" style={{ width: "380px" }}>
                                    <InputGroup className="tour-packages-search">
                                        <InputGroupAddon addonType="prepend">
                                            <InputGroupText style={{ padding: "5px 9px" }}>
                                                <i className="fas fa-search" style={{ fontSize: "15px" }} />
                                            </InputGroupText>
                                        </InputGroupAddon>

                                        <Input
                                            type="text"
                                            placeholder="Search by package name OR location..."
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            style={{
                                                height: "36px",
                                                padding: "5px 9px",
                                                fontSize: "12px"
                                            }}
                                        />

                                        {searchTerm && (
                                            <InputGroupAddon addonType="append">
                                                <InputGroupText
                                                    className="tour-packages-search-clear"
                                                    onClick={() => {
                                                        setSearchTerm("");
                                                        setCurrentPage(1);
                                                    }}
                                                    style={{ cursor: "pointer", padding: "5px 9px" }}
                                                >
                                                    <i className="fas fa-times" style={{ fontSize: "12px" }} />
                                                </InputGroupText>
                                            </InputGroupAddon>
                                        )}
                                    </InputGroup>
                                </div>
                                {/* TABLE */}
                                <div
                                    className="table-responsive tour-packages-table-wrapper"
                                    style={{ overflowX: "auto" }}
                                >
                                    <Table
                                        className="align-items-center tour-packages-table"
                                        hover
                                    >
                                        <thead>
                                            <tr>
                                                <th style={{ width: "65px" }}>Sr. No.</th>
                                                <th style={{ minWidth: "150px", maxWidth: "200px" }}>Package Name</th>
                                                <th style={{ minWidth: "120px" }}>Location</th>
                                                <th style={{ minWidth: "90px" }}>Price</th>
                                                <th style={{ width: "300px", minWidth: "220px", maxWidth: "350px" }}>Description</th>
                                                <th style={{ minWidth: "100px" }}>Status</th>
                                                <th className="text-center" style={{ minWidth: "90px" }}>Actions</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {loading && currentPackages.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center tour-packages-empty-state">
                                                        <div className="tour-packages-loading">
                                                            <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                                                        </div>
                                                        <p className="mt-2">Loading packages...</p>
                                                    </td>
                                                </tr>
                                            ) : currentPackages.length > 0 ? (
                                                currentPackages.map((pkg, index) => (
                                                    <tr key={pkg.id || pkg._id || index}>
                                                        <td>
                                                            <span className="tour-packages-index">
                                                                {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                                            </span>
                                                        </td>

                                                        <td style={{ maxWidth: "200px", whiteSpace: "normal" }}>
                                                            <div className="tour-packages-package-wrapper">
                                                                <div>
                                                                    <span
                                                                        className="tour-packages-package-name"
                                                                        title={pkg.name || "No Data Found"}
                                                                        style={{
                                                                            display: "-webkit-box",
                                                                            WebkitLineClamp: 2,
                                                                            WebkitBoxOrient: "vertical",
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            wordBreak: "break-word"
                                                                        }}
                                                                    >
                                                                        {pkg.name || "No Data Found"}
                                                                    </span>


                                                                </div>
                                                            </div>
                                                        </td>

                                                        <td>
                                                            <span className="tour-packages-destination">
                                                                <i
                                                                    style={{ color: "#5e72e4" }}
                                                                />
                                                                {pkg.destination || "No Data Found"}
                                                            </span>
                                                        </td>

                                                        <td>
                                                            <span className="font-weight-bold text-primary">
                                                                {pkg.price}
                                                            </span>
                                                        </td>
                                                        <td style={{ maxWidth: "320px", whiteSpace: "normal" }}>
                                                            <div
                                                                className="tour-package-description"
                                                                title={
                                                                    pkg.shortDescription ||
                                                                    "No description available"
                                                                }
                                                                style={{
                                                                    maxWidth: "320px",
                                                                    display: "-webkit-box",
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: "vertical",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "normal",
                                                                    lineHeight: "1.45",
                                                                    fontSize: "0.85rem",
                                                                    color: "#525f7f",
                                                                    wordBreak: "break-word"
                                                                }}
                                                            >
                                                                {pkg.shortDescription ||
                                                                    "No description available"}
                                                            </div>
                                                        </td>

                                                        <td>
                                                            <Badge
                                                                className="tour-packages-status-badge"
                                                                color={
                                                                    pkg.status === "active"
                                                                        ? "success"
                                                                        : "secondary"
                                                                }
                                                                pill
                                                            >
                                                                <i className="fas fa-circle mr-1" />
                                                                {pkg.status === "active"
                                                                    ? "Active"
                                                                    : "Inactive"}
                                                            </Badge>
                                                        </td>

                                                        <td className="text-center">
                                                            <UncontrolledDropdown className="table-action-dropdown">
                                                                <DropdownToggle
                                                                    className="action-three-dots-btn"
                                                                    size="sm"
                                                                    color=""
                                                                    caret={false}
                                                                >
                                                                    <i className="fas fa-ellipsis-v" />
                                                                </DropdownToggle>

                                                                <DropdownMenu right >

                                                                    {/* View Details */}
                                                                    <DropdownItem onClick={() => openViewModal(pkg)}>
                                                                        <i className="fas fa-eye text-info mr-2" />
                                                                        View Details
                                                                    </DropdownItem>

                                                                    {/* Activate / Deactivate */}
                                                                    <DropdownItem
                                                                        onClick={() => handleTogglePackageStatus(pkg.uuid)}
                                                                    >
                                                                        <i
                                                                            className={`fas ${pkg.isActive
                                                                                ? "fa-toggle-off text-danger"
                                                                                : "fa-toggle-on text-success"
                                                                                } mr-2`}
                                                                        />

                                                                        {pkg.isActive ? "Deactivate" : "Activate"}
                                                                    </DropdownItem>

                                                                </DropdownMenu>
                                                            </UncontrolledDropdown>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center tour-packages-empty-state">
                                                        <div className="tour-packages-empty-icon">
                                                            <i className="fas fa-box-open" />
                                                        </div>

                                                        {/* <h5>No Packages Found</h5> */}

                                                        <p>
                                                            {searchTerm
                                                                ? "No packages match your search."
                                                                : "No tour packages available yet."}
                                                        </p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>

                            {/* ================= FOOTER ================= */}
                            {!loading && totalItems > 0 && (
                                <CardFooter className="py-4 border-0">
                                    <CustomPagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        totalItems={totalItems}
                                        itemsPerPage={ITEMS_PER_PAGE}
                                        onPageChange={handlePageChange}
                                        itemName="tour packages"
                                    />
                                </CardFooter>
                            )}
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* =====================================================
                VIEW PACKAGE DETAIL MODAL
            ===================================================== */}
            <Modal
                isOpen={viewModalOpen}
                toggle={closeViewModal}
                centered
                size="lg"
                returnFocus={false}
                className="user-table-user-model"
            >
                {/* ==================== MODAL HEADER ==================== */}
                <ModalHeader toggle={closeViewModal}>
                    <div className="user-modal-header-content">
                        <div className="user-modal-header-icon">
                            <i className="fas fa-map-marked-alt"></i>
                        </div>

                        <div>
                            <h4 className="mb-0">
                                {selectedPackage?.packageName || "Package Details"}
                            </h4>

                            <small>Tour Package Details</small>
                        </div>
                    </div>
                </ModalHeader>

                {/* ==================== MODAL BODY ==================== */}
                <ModalBody>
                    {viewLoading ? (
                        /* ==================== LOADING ==================== */
                        <div className="text-center py-5">
                            <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>

                            <p className="mt-2 text-muted mb-0">
                                Loading package details...
                            </p>
                        </div>
                    ) : selectedPackage ? (
                        <>

                            {/* ==================== TOUR PHOTOS GALLERY GRID ==================== */}
                            {(() => {
                                const tourImages = getTourImages(selectedPackage);

                                return (
                                    <div className="user-modal-section mb-4">
                                        <div className="user-modal-section-title d-flex align-items-center justify-content-between">
                                            <div>
                                                <i className="fas fa-images"></i>
                                                <span>Tour Photos Gallery</span>
                                            </div>
                                            {tourImages.length > 0 && (
                                                <Badge color="primary" pill className="px-3 py-1" style={{ fontSize: "11px" }}>
                                                    {tourImages.length} {tourImages.length === 1 ? 'Photo' : 'Photos'}
                                                </Badge>
                                            )}
                                        </div>

                                        {tourImages.length > 0 ? (
                                            <div
                                                className="tour-gallery-grid mt-3"
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                                                    gap: "12px"
                                                }}
                                            >
                                                {tourImages.map((imgUrl, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="tour-gallery-item-card shadow-sm"
                                                        onClick={() => {
                                                            setPreviewImage(imgUrl);
                                                            setPreviewModalOpen(true);
                                                        }}
                                                        style={{
                                                            height: "120px",
                                                            borderRadius: "10px",
                                                            overflow: "hidden",
                                                            position: "relative",
                                                            cursor: "pointer",
                                                            border: "1px solid #e9ecef",
                                                            background: "#f8f9fe",
                                                            transition: "all 0.25s ease"
                                                        }}
                                                    >
                                                        <img
                                                            src={imgUrl}
                                                            alt={`Tour Photo ${idx + 1}`}
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                                transition: "transform 0.3s ease"
                                                            }}
                                                        />
                                                        <div
                                                            className="gallery-hover-overlay"
                                                            style={{
                                                                position: "absolute",
                                                                inset: 0,
                                                                background: "rgba(0, 0, 0, 0.35)",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                opacity: 0,
                                                                transition: "opacity 0.2s ease",
                                                                color: "#ffffff"
                                                            }}
                                                            onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                                                            onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
                                                        >
                                                            <i className="fas fa-search-plus" style={{ fontSize: "20px" }} />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            /* Empty state when no photos exist */
                                            <div
                                                className="no-tour-photo-banner p-4 mt-2 text-center"
                                                style={{
                                                    width: "100%",
                                                    borderRadius: "10px",
                                                    background: "#f8f9fe",
                                                    border: "1px dashed #cad1d7",
                                                    color: "#8898aa"
                                                }}
                                            >
                                                <i className="fas fa-images mb-2" style={{ fontSize: "32px", color: "#adb5bd" }} />
                                                <p className="mb-0 text-sm font-weight-bold" style={{ color: "#525f7f" }}>
                                                    No Tour Photos Uploaded
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}


                            <div className="user-modal-section mb-4">
                                <div className="user-modal-section-title">
                                    <i className="fas fa-box"></i>
                                    <span>Package Information</span>
                                </div>

                                <Row>
                                    {/* Package Name */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-suitcase"></i>
                                            </div>

                                            <div className="user-detail-content">
                                                <span>Package Name</span>

                                                <strong>
                                                    {selectedPackage.packageName ||
                                                        "No Data Found"}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Location */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-map-marker-alt"></i>
                                            </div>

                                            <div className="user-detail-content">
                                                <span>Location</span>

                                                <strong>
                                                    {selectedPackage.location ||
                                                        "No Data Found"}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Price */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-dollar-sign"></i>
                                            </div>

                                            <div className="user-detail-content">
                                                <span>Price</span>

                                                <strong>
                                                    {selectedPackage.price ?? 0}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Category */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-layer-group"></i>
                                            </div>

                                            <div className="user-detail-content">
                                                <span>Category</span>

                                                <strong>
                                                    {selectedPackage.category?.name ||
                                                        selectedPackage.categoryName ||
                                                        "No Data Found"}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Status */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-toggle-on"></i>
                                            </div>

                                            <div className="user-detail-content">
                                                <span>Status</span>

                                                <Badge
                                                    className={`custom-badge ${selectedPackage.isActive
                                                        ? "badge-active"
                                                        : "badge-pending"
                                                        }`}
                                                    pill
                                                >
                                                    {selectedPackage.isActive
                                                        ? "Active"
                                                        : "Inactive"}
                                                </Badge>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Created At */}
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-calendar-alt"></i>
                                            </div>

                                            <div className="user-detail-content">
                                                <span>Created At</span>

                                                <strong>
                                                    {selectedPackage.createdAt
                                                        ? formatDate(
                                                            selectedPackage.createdAt
                                                        )
                                                        : "No Data Found"}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* =====================================================
                    DESCRIPTION
                ====================================================== */}
                            <div className="user-modal-section mb-4">
                                <div className="user-modal-section-title">
                                    <i className="fas fa-align-left"></i>
                                    <span>Description</span>
                                </div>

                                <div className="user-detail-item user-detail-full">
                                    <div className="user-detail-icon">
                                        <i className="fas fa-file-alt"></i>
                                    </div>

                                    <div className="user-detail-content">
                                        <span>Short Description</span>

                                        <strong>
                                            {selectedPackage.shortDescription ||
                                                "No description available."}
                                        </strong>
                                    </div>
                                </div>
                            </div>

                            {/* =====================================================
                    GUIDE DETAILS
                ====================================================== */}
                            <div className="user-modal-section mb-4">
                                <div className="user-modal-section-title">
                                    <i className="fas fa-user-tie"></i>
                                    <span>Guide Details</span>
                                </div>

                                {/* Guide Profile */}
                                <div
                                    className="user-detail-item user-detail-full"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {/* GUIDE IMAGE */}
                                    <div
                                        style={{
                                            width: "65px",
                                            height: "65px",
                                            minWidth: "65px",
                                            borderRadius: "50%",
                                            overflow: "hidden",
                                            background: "#f5f7ff",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            marginRight: "15px",
                                            border: "2px solid #e9ecff",
                                        }}
                                    >
                                        {selectedPackage?.guide?.profileImage ||
                                            selectedPackage?.guide?.profilePicture ||
                                            selectedPackage?.guide?.image ||
                                            selectedPackage?.guide?.avatar ? (
                                            <img
                                                src={
                                                    selectedPackage.guide
                                                        .profileImage ||
                                                    selectedPackage.guide
                                                        .profilePicture ||
                                                    selectedPackage.guide.image ||
                                                    selectedPackage.guide.avatar
                                                }
                                                alt={
                                                    selectedPackage.guide?.fullName ||
                                                    selectedPackage.guide?.name ||
                                                    "Guide"
                                                }
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    borderRadius: "50%",
                                                }}
                                                onError={(e) => {
                                                    e.currentTarget.style.display =
                                                        "none";

                                                    if (
                                                        e.currentTarget
                                                            .nextElementSibling
                                                    ) {
                                                        e.currentTarget.nextElementSibling.style.display =
                                                            "flex";
                                                    }
                                                }}
                                            />
                                        ) : null}

                                        {/* Guide Image Fallback */}
                                        <div
                                            style={{
                                                display:
                                                    selectedPackage?.guide
                                                        ?.profileImage ||
                                                        selectedPackage?.guide
                                                            ?.profilePicture ||
                                                        selectedPackage?.guide?.image ||
                                                        selectedPackage?.guide?.avatar
                                                        ? "none"
                                                        : "flex",
                                                width: "100%",
                                                height: "100%",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <i
                                                className="fas fa-user"
                                                style={{
                                                    fontSize: "28px",
                                                    color: "#5e72e4",
                                                }}
                                            ></i>
                                        </div>
                                    </div>

                                    {/* Guide Name */}
                                    <div className="user-detail-content">
                                        <span>Guide Name</span>

                                        <strong>
                                            {selectedPackage.guide?.fullName ||
                                                selectedPackage.guide?.name ||
                                                "No Data Found"}
                                        </strong>
                                    </div>
                                </div>

                                {/* Guide Email & Phone */}
                                <Row className="mt-2">
                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-envelope"></i>
                                            </div>

                                            <div className="user-detail-content">
                                                <span>Email</span>

                                                <strong>
                                                    {selectedPackage.guide?.email ||
                                                        "No Data Found"}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>

                                    <Col md="6">
                                        <div className="user-detail-item">
                                            <div className="user-detail-icon">
                                                <i className="fas fa-phone"></i>
                                            </div>

                                            <div className="user-detail-content">
                                                <span>Phone Number</span>

                                                <strong>
                                                    {selectedPackage.guide
                                                        ?.mobileNumber ||
                                                        "No Data Found"}
                                                </strong>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* =====================================================
                    PLACES
                ====================================================== */}
                            <div className="user-modal-section mb-4">
                                <div className="user-modal-section-title">
                                    <i className="fas fa-map-signs"></i>
                                    <span>Places</span>
                                </div>

                                {Array.isArray(selectedPackage.places) &&
                                    selectedPackage.places.length > 0 ? (
                                    <Row>
                                        {selectedPackage.places.map(
                                            (place, index) => (
                                                <Col
                                                    md="6"
                                                    key={place._id || index}
                                                >
                                                    <div className="user-detail-item">
                                                        <div className="user-detail-icon">
                                                            <i className="fas fa-map-marker-alt"></i>
                                                        </div>

                                                        <div className="user-detail-content">
                                                            <span>
                                                                Place {index + 1}
                                                            </span>

                                                            <strong>
                                                                {place.placeName ||
                                                                    "No Data Found"}
                                                            </strong>

                                                            {place.description && (
                                                                <small className="d-block mt-1 text-muted">
                                                                    {
                                                                        place.description
                                                                    }
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>
                                            )
                                        )}
                                    </Row>
                                ) : (
                                    <div className="user-detail-item user-detail-full">
                                        <div className="user-detail-icon">
                                            <i className="fas fa-map-marker-alt"></i>
                                        </div>

                                        <div className="user-detail-content">
                                            <span>Places</span>

                                            <strong>
                                                No places available
                                            </strong>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        /* ==================== NO PACKAGE ==================== */
                        <div className="user-empty-state">
                            <i className="fas fa-map-marked-alt"></i>

                            <p>No package data available</p>
                        </div>
                    )}
                </ModalBody>

                {/* ==================== MODAL FOOTER ==================== */}
                <ModalFooter className="tour-packages-modal-footer">
                    <Button
                        className="user-action-btn user-view-btn"
                        size="sm"
                        onClick={closeViewModal}
                        color="secondary"
                    >
                        <i className="fas fa-times mr-1"></i>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ==================== IMAGE LIGHTBOX PREVIEW MODAL ==================== */}
            <Modal
                isOpen={previewModalOpen}
                toggle={() => setPreviewModalOpen(false)}
                centered
                size="lg"
                className="tour-image-preview-modal"
            >
                <ModalHeader toggle={() => setPreviewModalOpen(false)}>
                    <span className="font-weight-bold">Tour Photo Preview</span>
                </ModalHeader>
                <ModalBody className="text-center p-2 bg-dark">
                    {previewImage && (
                        <img
                            src={previewImage}
                            alt="Tour Photo Preview"
                            style={{
                                maxWidth: "100%",
                                maxHeight: "75vh",
                                borderRadius: "8px",
                                objectFit: "contain"
                            }}
                        />
                    )}
                </ModalBody>
            </Modal>
        </>
    );
};

export default TourPackages;